import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { OverdueFee } from '../../types/fees';
import { feesApi } from '../../services/feesApi';
import './OverdueFeesWidget.css';

interface OverdueFeesWidgetProps {
  maxItems?: number;
  showViewAll?: boolean;
}

const OverdueFeesWidget: React.FC<OverdueFeesWidgetProps> = ({ 
  maxItems = 5, 
  showViewAll = true 
}) => {
  const [overdueFees, setOverdueFees] = useState<OverdueFee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalOverdue, setTotalOverdue] = useState(0);

  const fetchOverdueFees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await feesApi.getOverdue();
      setOverdueFees(response.data.overdueFeees.slice(0, maxItems));
      setTotalOverdue(response.data.total);
    } catch (error: any) {
      console.error('Error fetching overdue fees:', error);
      setError('Failed to load overdue fees');
    } finally {
      setLoading(false);
    }
  }, [maxItems]);

  useEffect(() => {
    fetchOverdueFees();
  }, [fetchOverdueFees]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysOverdueColor = (days: number) => {
    if (days <= 7) return 'warning';
    if (days <= 30) return 'danger';
    return 'critical';
  };

  if (loading) {
    return (
      <div className="overdue-fees-widget">
        <div className="widget-header">
          <h3>⚠️ Overdue Fees</h3>
        </div>
        <div className="widget-loading">
          <div className="loading-spinner"></div>
          <p>Loading overdue fees...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="overdue-fees-widget">
        <div className="widget-header">
          <h3>⚠️ Overdue Fees</h3>
        </div>
        <div className="widget-error">
          <div className="error-icon">❌</div>
          <p>{error}</p>
          <button className="retry-btn" onClick={fetchOverdueFees}>
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="overdue-fees-widget">
      <div className="widget-header">
        <div className="header-content">
          <h3>⚠️ Overdue Fees</h3>
          {totalOverdue > 0 && (
            <span className="total-badge">{totalOverdue}</span>
          )}
        </div>
        <button className="refresh-btn" onClick={fetchOverdueFees} title="Refresh">
          🔄
        </button>
      </div>

      <div className="widget-body">
        {overdueFees.length === 0 ? (
          <div className="no-overdue">
            <div className="no-overdue-icon">✅</div>
            <h4>All fees are current!</h4>
            <p>No overdue payments at this time.</p>
          </div>
        ) : (
          <>
            <div className="overdue-list">
              {overdueFees.map((fee) => (
                <div key={fee.id} className="overdue-item">
                  <div className="student-info">
                    <div className="student-name">{fee.studentName}</div>
                    <div className="fee-description">{fee.feeDescription}</div>
                  </div>
                  
                  <div className="fee-details">
                    <div className="amount">{formatCurrency(fee.amount)}</div>
                    <div className="due-info">
                      <span className="due-date">Due: {formatDate(fee.dueDate)}</span>
                      <span className={`days-overdue ${getDaysOverdueColor(fee.daysOverdue)}`}>
                        {fee.daysOverdue} day{fee.daysOverdue !== 1 ? 's' : ''} overdue
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {totalOverdue > maxItems && (
              <div className="more-items">
                <span className="more-text">
                  +{totalOverdue - maxItems} more overdue fee{totalOverdue - maxItems !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {showViewAll && (
        <div className="widget-footer">
          <Link to="/fees-management?status=overdue" className="view-all-btn">
            View All Overdue Fees →
          </Link>
        </div>
      )}
    </div>
  );
};

export default OverdueFeesWidget;